/*  
    Developers: Sakshi Bhise  
    Script: parseAnswer.ts  
    Date: 06/18/2025  
    Project Name: AskHR  
    Team: DSET  
  
    Purpose of this script:  
    This utility module provides the parseAnswer function, which transforms the raw AskResponse object (received from the backend)  
    into a ParsedAnswer format that separates out citations and prepares markdown content for rendering.  
    Centralizing this transformation decouples backend structure from frontend presentation and allows for easier extension of parsing logic in the future.  
*/  
  
//--------------------- Import Types ---------------------//  
import { AskResponse, Citation } from "../../api";  
  
//--------------------- Output Type for Parsed Answer ---------------------//  
type ParsedAnswer = {  
    citations: Citation[];             // Array of source citations attached to this answer  
    markdownFormatText: string;        // The AI's answer, ready for markdown rendering  
};  
  
//--------------------- Main Parsing Function ---------------------//  
/**  
 * Converts an AskResponse into a ParsedAnswer ready for display.  
 * If you need to rewrite/extend parsing logic (e.g., extracting citations from markdown), do it here.  
 */  
export function parseAnswer(answer: AskResponse): ParsedAnswer {
    let answerText = answer.answer; // Raw response text (markdown, etc.)

    // Wrap bare email addresses as explicit markdown links before DOMPurify sanitization.
    // DOMPurify encodes & to &amp; in plain text, which breaks remark-gfm autolink detection
    // for emails like (only the part after & gets auto-linked).
    // By pre-wrapping as [email](mailto:email), ReactMarkdown decodes &amp; back to & when rendering.
    answerText = answerText.replace(
        /\[([^\]]+)\]\(mailto:([^)]+)\)|([a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
        (match, _linkText, _linkHref, bareEmail) => {
            if (bareEmail) {
                return `[${bareEmail}](mailto:${bareEmail})`;
            }
            return match; // Already a markdown mailto link — leave unchanged
        }
    );

    return {
        citations: answer.citations,     // Direct pass-through of citations array
        markdownFormatText: answerText   // Not altered here, but could preprocess
    };
}  